import { FC, useMemo } from "react";
import StarRateNewTrustScore from "../../../components/StarRate2.0";
import { scoreToDisplayStars } from "../../../config/rating";
import { IWidgetTemplate } from "../../../interfaces/widget-template";
import { ReachClick } from "../../../utils/core";
import { resolveWidgetV2Style } from "./widgetV2Styles";

/** Same asymmetric rounding as widget-v2.js's roundHalfStars / the design system's roundHalf. */
const roundHalfStars = (stars: number): number => {
  const f = Math.floor(stars);
  const frac = stars - f;
  if (frac >= 0.5 - 1e-9) return f + 1;
  if (frac >= 0.25 - 1e-9) return f + 0.5;
  return f;
};

const starBand = (stars: number): string => {
  if (stars <= 0) return "#d5dae2";
  if (stars <= 1.49) return "#FF6060";
  if (stars <= 2.49) return "#FF9B45";
  if (stars <= 3.49) return "#FFD050";
  if (stars <= 4.49) return "#5596FF";
  return "#4060FF";
};

const starLabel = (stars: number): string => {
  if (stars <= 0) return "No score";
  if (stars < 1) return "Avoid";
  if (stars < 2) return "Beware";
  if (stars < 3) return "Average";
  if (stars < 4) return "Good";
  return "Trusted";
};

/** Colored rating-label pill next to ScoreRing's number — ports widget-v2.js's starLabelPillHtml. */
const StarLabelPill: FC<{ stars: number; size?: number }> = ({ stars, size = 10 }) => {
  const rounded = roundHalfStars(stars);
  const bg = starBand(rounded);
  const yellow = rounded > 2.49 && rounded <= 3.49;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "1px 8px",
        fontWeight: 700,
        background: bg,
        color: yellow ? "#101223" : "#fff",
        fontSize: size,
      }}
    >
      {starLabel(stars)}
    </span>
  );
};

/** Score Ring (v2) — admin-preview-only. Real embed markup lives in widget-v2.js (renderRing). */
const WidgetTemplate18: FC<IWidgetTemplate> = ({ company, score, style }) => {
  const companyId = company?.companyId;
  const resolvedStyle = resolveWidgetV2Style("ring", style);
  const payload = useMemo(() => ({ companyId, widgetId: 18 as const }), [companyId]);
  const profileUrl = useMemo(
    () => `https://www.trustfinance.com/companies/${companyId}?utm_source=widget&utm_medium=dom&utm_campaign=widget-v2-ring`,
    [companyId],
  );
  const scoreValue = score?.score ?? 0;

  const ring = (size: number, stroke: number, trackColor: string) => {
    const r = size / 2 - 7;
    const c = 2 * Math.PI * r;
    const offset = c * (1 - scoreValue / 100);
    return (
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#006bf1" strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} />
      </svg>
    );
  };

  if (resolvedStyle === "dark-tile") {
    return (
      <div className="d-flex justify-content-center">
        <a
          href={profileUrl}
          target="_blank"
          rel="noopener"
          onClick={() => ReachClick(payload)}
          className="d-flex align-items-center gap-3"
          style={{ textDecoration: "none", background: "#1c1c2e", borderRadius: 8, padding: "12px 16px" }}
        >
          {ring(44, 7, "#33334a")}
          <div style={{ color: "#fff", fontSize: 11, lineHeight: 1.3 }}>
            TrustScore<br /><b style={{ fontSize: 13 }}>{scoreValue} / 100</b>
          </div>
        </a>
      </div>
    );
  }

  return (
    <div className="d-flex justify-content-center">
      <a
        href={profileUrl}
        target="_blank"
        rel="noopener"
        onClick={() => ReachClick(payload)}
        className="d-flex align-items-center gap-4"
        style={{ textDecoration: "none", color: "#1c1c2e" }}
      >
        {ring(56, 9, "#eef0f3")}
        <div>
          <div style={{ fontWeight: 700, fontSize: 30, lineHeight: 1 }}>
            {scoreValue}
            <span style={{ fontSize: 14, color: "#9aa0b4" }}>/100</span>
          </div>
          <div className="d-flex align-items-center" style={{ gap: 6, margin: "4px 0" }}>
            <StarRateNewTrustScore value={score?.score} size={14} />
            <b style={{ fontSize: 12 }}>{scoreToDisplayStars(scoreValue)}</b>
            <StarLabelPill stars={scoreValue / 20} size={10} />
          </div>
          {resolvedStyle === "with-breakdown" && (
            <div style={{ fontSize: 11, color: "#6b6b85" }}>Review 60% · Licence &amp; LMP 40%</div>
          )}
        </div>
      </a>
    </div>
  );
};

export default WidgetTemplate18;
